import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Pause, Music2, Crown } from "lucide-react";
import { api, mediumArt, bigArt } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { useMeta } from "@/lib/useMeta";

const PublicPlaylist = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { current, playing, togglePlay, playTracks } = usePlayer();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/p/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  const ogImage = bigArt(data?.playlist?.tracks?.[0]?.artworkUrl100) || undefined;
  const tracksLen = data?.playlist?.tracks?.length || 0;
  useMeta({
    title: data?.playlist
      ? `${data.playlist.name} · 1o1sound`
      : "Playlist · 1o1sound",
    description: data?.playlist
      ? `${tracksLen} song${tracksLen === 1 ? "" : "s"} curated by ${data.owner?.name || "Anonymous"} on 1o1sound. Listen now.`
      : "Listen to this playlist on 1o1sound.",
    image: ogImage,
  });

  if (loading)
    return <div className="min-h-screen bg-[#050505] text-white/40 flex items-center justify-center">Loading...</div>;
  if (!data?.playlist)
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8 text-center">
        <Music2 size={48} className="text-white/20 mb-4" />
        <h1 className="font-display text-3xl">Playlist not found</h1>
        <p className="text-white/50 mt-2">This link may be invalid or the playlist was deleted.</p>
        <Link to="/" className="mt-6 bg-white text-black rounded-full px-6 py-2.5 font-semibold">Go home</Link>
      </div>
    );

  const { playlist, owner } = data;
  const tracks = playlist.tracks || [];
  const playable = tracks.filter((t) => t.previewUrl);
  const cover = tracks[0]?.artworkUrl100;
  const isPlPlaying = playing && playable.some((t) => t.trackId === current?.trackId);

  return (
    <div data-testid="public-playlist-page" className="min-h-screen bg-[#050505] text-white pb-32">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_audio-flow-133/artifacts/e1fop4m5_IMG_7311.png"
            alt="1o1sound"
            className="w-9 h-9 rounded-lg object-contain bg-black"
          />
          <span className="font-display text-lg font-semibold bg-gradient-to-r from-[#B580FF] via-[#FF2E00] to-[#5E8BFF] bg-clip-text text-transparent">1o1sound</span>
        </Link>
        <Link
          to="/premium"
          className="text-xs font-semibold bg-white text-black rounded-full px-4 py-1.5 flex items-center gap-1.5 hover:scale-[1.03] transition"
        >
          <Crown size={12} /> Try free
        </Link>
      </header>

      {/* Hero */}
      <div className="relative px-6 sm:px-12 py-12 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background: `radial-gradient(900px circle at 30% 30%, rgba(255,51,102,0.35), transparent 60%), radial-gradient(700px circle at 75% 70%, rgba(157,76,221,0.3), transparent 60%), #050505`,
          }}
        />
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end max-w-5xl mx-auto">
          <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-lg shadow-2xl overflow-hidden bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center flex-shrink-0">
            {cover ? <img src={mediumArt(cover)} alt="" className="w-full h-full object-cover" /> : <Music2 size={64} className="text-black/60" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2">Public Playlist</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-none break-words">{playlist.name}</h1>
            {playlist.description && <p className="text-white/70 mt-3">{playlist.description}</p>}
            <div className="flex items-center gap-2 mt-4 text-sm text-white/60">
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                {owner.picture ? <img src={owner.picture} alt="" className="w-full h-full object-cover" /> : <span>{owner.name?.[0]?.toUpperCase()}</span>}
              </div>
              <span className="font-medium text-white">{owner.name}</span>
              <span>·</span>
              <span>{tracks.length} song{tracks.length === 1 ? "" : "s"}</span>
            </div>

            <button
              data-testid="public-play-btn"
              onClick={() => {
                if (playable.length === 0) return;
                if (isPlPlaying) togglePlay();
                else playTracks(playable, 0);
              }}
              disabled={playable.length === 0}
              className="mt-6 inline-flex items-center gap-2 bg-[#FF2E00] text-white font-semibold rounded-full px-7 py-3 hover:bg-[#E62900] hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition-all disabled:opacity-30"
            >
              {isPlPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
              {isPlPlaying ? "Pause" : "Play"}
            </button>
          </div>
        </div>
      </div>

      {/* Tracks */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 mt-4">
        {tracks.length === 0 ? (
          <p className="text-white/50 text-center py-12">This playlist is empty.</p>
        ) : (
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
            {tracks.map((t, i) => {
              const isCurrent = current?.trackId === t.trackId;
              return (
                <button
                  key={`${t.trackId}-${i}`}
                  data-testid={`pub-track-${t.trackId}`}
                  onClick={() => {
                    if (!t.previewUrl) return;
                    if (isCurrent) togglePlay();
                    else {
                      const idx = playable.findIndex((p) => p.trackId === t.trackId);
                      playTracks(playable, idx >= 0 ? idx : 0);
                    }
                  }}
                  className="w-full grid grid-cols-[28px_auto_1fr_auto] sm:grid-cols-[28px_auto_1fr_auto_auto] items-center gap-4 px-4 sm:px-6 py-3 hover:bg-white/5 transition text-left"
                >
                  <span className="text-sm text-white/40 tabular-nums">{i + 1}</span>
                  <img src={mediumArt(t.artworkUrl100)} alt="" className="w-10 h-10 rounded object-cover" />
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isCurrent ? "text-[#FF2E00]" : ""}`}>{t.trackName}</p>
                    <p className="text-xs text-white/50 truncate">{t.artistName}</p>
                  </div>
                  <span className="text-xs text-white/40 hidden sm:inline truncate max-w-[200px]">{t.collectionName}</span>
                  <span className="text-xs text-white/40 font-mono tabular-nums">
                    {t.trackTimeMillis ? `${Math.floor(t.trackTimeMillis / 60000)}:${Math.floor((t.trackTimeMillis % 60000) / 1000).toString().padStart(2, "0")}` : "0:30"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA footer */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 mt-10">
        <Link
          to="/premium"
          className="block rounded-2xl bg-gradient-to-br from-[#FF2E00]/20 via-[#FF2E00]/15 to-[#E2FF31]/10 border border-white/10 p-6 hover:border-white/20 transition"
        >
          <div className="flex items-center gap-4">
            <Crown size={28} className="text-[#E2FF31] flex-shrink-0" />
            <div className="flex-1">
              <p className="font-display text-xl font-semibold">Like what you hear?</p>
              <p className="text-sm text-white/60">Get 2 months free on 1o1sound Premium — unlimited playlists, ad-free, $10/mo after.</p>
            </div>
            <span className="bg-white text-black font-semibold rounded-full px-5 py-2 text-sm">Try free</span>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default PublicPlaylist;
